import { useContext, useEffect, useState } from 'react';
import PageInfo from '../../molecules/page-info/page-info';
import { UserContext } from '../../../contexts/userContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WCircularProgress } from '../../wrappers/wrapper';
import { localStorageService, STORAGE_KEY } from '../../../services/local-storage.service';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import styles from './channel-bar.module.scss';
import { IPaginate, TSpace, TUser } from '../../../models/common';
import dbService from '../../../services/database.service';
import { CHANNEl_IDS, DEFAULT_HTTP_ERR_MESSAGE } from '../../../utils/constants';
import NewPost from '../new-post/new-post';
import ChannelBarMenu from 'components/organisms/channel-bar-menu/channel-bar-menu';
import SpaceMembers from 'components/molecules/space-members/space-members';
import useDeviceType from 'custom-hooks/useDeviceType';
import Button from 'components/atoms/button/button';
import helperService from 'services/helper.service';
import ConfirmDialog from 'components/organisms/confirm-dialog/confirm-dialog';
import authService from '../../../services/auth.service';

function InformativeTitle() {
  const { isDesktop } = useDeviceType();
  const isLoggedIn = authService.isLoggedIn();
  const { currentPage, user } = useContext(UserContext);
  const { article_id, userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [spaces, setSpaces] = useState<IPaginate<TSpace> | null>();
  const [space, setSpace] = useState<TSpace>();
  const [showJoin, setShowJoin] = useState<boolean>(false);
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({
    data: true,
  });
  const [totalMembers, setTotalMembers] = useState(0);
  const [isLeaving, setIsLeaving] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const channelName = location.pathname.split('/')[2]?.toUpperCase();
  const token = localStorageService.getItem(STORAGE_KEY.TOKEN);
  const isArticlesPage = /^\/c\/[^/]+\/articles$/.test(location.pathname);
  const isHomePage = location.pathname === '/home';
  const isButtonVisible = token && location.pathname !== '/members' && location.pathname !== '/documentation' && !isArticlesPage;

  const isMemberPage = currentPage.title === 'Members';
  const showBackButton = article_id || userId;

  useEffect(() => {
    initializeData();
  }, [channelName]);

  useEffect(() => {
    if (isMemberPage) {
      fetchMembersData();
    }
  }, [currentPage.title]);

  const initializeData = () => {
    setLoading({ data: true });
    setSpaces(null);
    getData().finally(() => setLoading({ data: false, join: false }));
  };

  const getData = async () => {
    if (location.pathname === '/members' || location.pathname === '/home' || location.pathname === '/documentation' || isArticlesPage) {
      setShowJoin(false);
      setSpaces(null);
      return;
    }

    const res = await dbService
      .getSpaces({
        filter: { channel: CHANNEl_IDS[channelName] },
        relation: true,
        paginate: true,
        limit: 3,
      })
      .then((res) => res as unknown as IPaginate<TSpace>);

    setSpaces(res);

    const space = await getSpace(user._id!);
    setSpace(space);

    const isJoin = !!space;
    setShowJoin(!isJoin);
    setSpaces(isJoin ? res : null);
  };

  const getSpace = (userId: string) => {
    return dbService
      .getSpaces({
        filter: { channel: CHANNEl_IDS[channelName], user: userId },
      })
      .then((res) => res[0]);
  };

  const joinChannel = async () => {
    setLoading({ ...loading, join: true });
    try {
      await dbService.insertSpace({
        user: user._id,
        channel: CHANNEl_IDS[channelName],
      });
      initializeData();
    } catch (error) {
      setLoading({ ...loading, join: false });
    }
  };

  const handleLeaveSpace = () => {
    if (!space?._id) return;

    setIsLeaving(true);
    dbService
      .removeSpace(space?._id)
      .then((_) => {
        setShowJoin(true);
        initializeData();
        setIsLeaving(false);
      })
      .catch((err) => {
        helperService.showToast('error', DEFAULT_HTTP_ERR_MESSAGE);
      });
  };

  const handleLeaveSpaceClick = () => {
    setOpenConfirmDialog(true);
  };

  const fetchMembersData = async () => {
    const resPaginate = await dbService
      .getUsers({ paginate: true, limit: 1 })
      .then((res) => res as unknown as IPaginate<TUser>);

    setTotalMembers(resPaginate.meta.total);
  };

  const handleClickMembers = () => {
    navigate('/members');
  };

  const pageTitle = `${currentPage.title}${isMemberPage && totalMembers ? ` (${totalMembers})` : ''}`;

  return (
    <div className={styles.channelBar}>
      <div className={styles.left}>
        {showBackButton ? (
          <Button type="base" onClick={() => navigate(-1)} className={styles.back}>
            <>
              <KeyboardBackspaceIcon />
              <span>{`Back to ${currentPage.title}`}</span>
            </>
          </Button>
        ) : (
          <PageInfo icon={currentPage.icon} title={pageTitle} />
        )}
      </div>
      {loading.data
        ? null
        : !showBackButton && (
            <div className={`${styles.right} ${isLeaving ? styles.leaveAnimation : ''}`}>
              {isDesktop && !showJoin && spaces && (
                <SpaceMembers
                  totalCount={spaces?.meta?.total!}
                  data={spaces?.data!}
                  onClick={handleClickMembers}
                />
              )}
              {showJoin && isLoggedIn ? (
                <Button
                  type={isDesktop ? 'primary' : 'base'}
                  radius="2rem"
                  onClick={joinChannel}
                  className={styles.joinButton}
                >
                  {loading.join ? <WCircularProgress size={20} color="inherit" /> : 'Join'}
                </Button>
              ) : (
                isButtonVisible && (
                  <>
                    <NewPost />
                    {!isHomePage && (
                      <ChannelBarMenu
                        spaces={spaces!}
                        onLeaveSpace={handleLeaveSpaceClick}
                        onClickMembers={handleClickMembers}
                      />
                    )}
                  </>
                )
              )}
            </div>
          )}

      <ConfirmDialog
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={handleLeaveSpace}
        title="Leave this space"
        text={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <span>Leaving the Support space will stop notifications from this space.</span>
            <span>
              To confirm, type <b>LEAVE</b> in the textbox below.
            </span>
          </div>
        }
        confirmText="Confirm"
        isLeaveAction={true}
        confirmationInputText="LEAVE"
      />
    </div>
  );
}

export default InformativeTitle;
